import * as React from "react";
import { Alert, DialogContentText, Stack } from "@mui/material";
import { useLanguage } from "./Language";
import axios from "axios";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { FormProvider, set, useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { FormTextField } from "@tlc/forms/FormTextField";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";
import { ConfirmDialog } from "@tlc/ui/dialog/ConfirmDialog";

export interface ResponseStatus {
  success: boolean;
  message: string;
  timeout: boolean;
}

export interface AbstractBaseResponse {
  status: ResponseStatus;
}

interface SendResetPINEmailResponse extends AbstractBaseResponse {}

interface ForgotPINFormInputs {
  barcode: string;
}

export const useForgotPINDialog = () => {
  const { open, closeDialog, openDialog, dialogProps } = useDialog();
  const {
    closeDialog: closeSuccessDialog,
    openDialog: openSuccessDialog,
    dialogProps: successDialogProps,
  } = useDialog();

  const { handleRequestError } = useErrorDialog();
  const { translate } = useLanguage();
  const formMethods = useForm<ForgotPINFormInputs>({
    defaultValues: { barcode: null },
  });
  const { handleSubmit, setFocus, reset } = formMethods;

  const onSubmit = useCallback(
    (data: ForgotPINFormInputs) => {
      const { barcode } = data;
      axios
        .get<SendResetPINEmailResponse>("/account/sendPinResetEmail", {
          params: {
            borrowerId: barcode,
          },
        })
        .then((response) => {
          if (response.data.status.success) {
            openSuccessDialog();
          }
          // TODO additional error handler here?
          closeDialog();
        })
        .catch((error: unknown) => {
          console.error("An unexpected error occurred:", error);
          handleRequestError(error);
        });
    },
    [handleRequestError],
  );

  useEffect(() => {
    if (open) {
      reset();
      requestAnimationFrame(() => setFocus("barcode"));
    }
  }, [setFocus, open, reset]);

  const formId = "forgot-pin-form";

  const ForgotPINDialog = (
    <>
      <ConfirmDialog
        id="forgot-pin-success"
        headline={translate("forgotPIN")}
        useSingleAction
        {...successDialogProps}
        confirmButtonText={translate("ok")}
        onConfirm={closeSuccessDialog}
      >
        <Alert severity="success">{translate("requestPINResetResponse")}</Alert>
      </ConfirmDialog>
      <FormDialog
        formId={formId}
        headline={translate("forgotPIN")}
        confirmButtonText={translate("resetPIN")}
        {...dialogProps}
      >
        <FormProvider {...formMethods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
            <DialogContentText>
              {translate("forgotPINFormInstructions")}
            </DialogContentText>
            <Stack
              sx={{
                gap: 2,
                paddingTop: 2,
              }}
            >
              <FormTextField<ForgotPINFormInputs>
                required
                fullWidth
                sx={{ width: "100%", maxWidth: "auto" }}
                maxLength={255}
                name={"barcode"}
                label={translate("barcode")}
              />
            </Stack>
          </form>
        </FormProvider>
      </FormDialog>
    </>
  );

  return {
    openForgotPinDialog: openDialog,
    ForgotPINDialog,
  };
};
